import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

import { StorageKey } from '../storage/storage.keys';
import { StorageService } from '../storage/storage.service';
import { isThemeMode, type ResolvedTheme, type ThemeMode } from './theme.models';

const DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({ providedIn: 'root' })
export class ThemeStore {
  private readonly storage = inject(StorageService);
  private readonly window = inject(DOCUMENT).defaultView;

  private readonly modeSignal = signal<ThemeMode>(this.restore());
  private readonly systemPrefersDarkSignal = signal(false);

  readonly mode = this.modeSignal.asReadonly();

  readonly systemPrefersDark = this.systemPrefersDarkSignal.asReadonly();

  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const mode = this.modeSignal();

    if (mode !== 'system') {
      return mode;
    }

    return this.systemPrefersDarkSignal() ? 'dark' : 'light';
  });

  readonly isDark = computed(() => this.resolvedTheme() === 'dark');

  constructor() {
    this.watchSystemPreference();
  }

  setMode(mode: ThemeMode): void {
    this.modeSignal.set(mode);
    this.storage.write(StorageKey.THEME_MODE, mode);
  }

  private restore(): ThemeMode {
    const stored = this.storage.read(StorageKey.THEME_MODE);

    return isThemeMode(stored) ? stored : 'system';
  }

  private watchSystemPreference(): void {
    const media = this.window?.matchMedia?.(DARK_QUERY);

    if (!media) {
      return;
    }

    this.systemPrefersDarkSignal.set(media.matches);

    const listener = (event: MediaQueryListEvent): void => {
      this.systemPrefersDarkSignal.set(event.matches);
    };

    media.addEventListener('change', listener);
    inject(DestroyRef).onDestroy(() => media.removeEventListener('change', listener));
  }
}
